import { Theme } from "@mui/material";
import {
	ExtendedMeasurableValueDTO,
	RootCauseWidgetWaterfallDTO,
} from "../../../../datasources/api-client/data-contracts";

export type WaterfallItemType = "increase" | "decrease" | "baseline" | "comparison";

export type WaterfallChartItem = ExtendedMeasurableValueDTO & {
	key: string;
	name: string;
	color: string;
	type: WaterfallItemType;
	waterfallRange: [number, number];
};

export type WaterfallKey =
	| "baseline"
	| "reportingScope"
	| "weightScope"
	| "distanceScope"
	| "modalScope"
	| "fuelQty"
	| "fuelMix"
	| "comparison";

const waterfallKeys: WaterfallKey[] = [
	"baseline",
	"reportingScope",
	"weightScope",
	"distanceScope",
	"modalScope",
	"fuelQty",
	"fuelMix",
	"comparison",
];

export const computeWaterfallData = (
	data: RootCauseWidgetWaterfallDTO | undefined,
	theme: Theme,
	labels: Record<WaterfallKey, string>
): WaterfallChartItem[] => {
	if (!data) {
		return [];
	}

	let runningTotal = 0;

	return waterfallKeys.reduce<WaterfallChartItem[]>((result, key) => {
		const item = data[key];

		if (!item) {
			return result;
		}

		const value = item.value ?? 0;

		// Baseline is an absolute value and always starts from zero.
		if (key === "baseline") {
			runningTotal = value;

			result.push({
				...item,
				key,
				name: labels[key],
				value,
				type: "baseline",
				color: theme.palette.info.light,
				waterfallRange: [0, value],
			});

			return result;
		}

		// Comparison is an absolute value and always starts from zero.
		if (key === "comparison") {
			result.push({
				...item,
				key,
				name: labels[key],
				value,
				type: "comparison",
				color: theme.palette.info.main,
				waterfallRange: [0, value],
			});

			return result;
		}

		// Intermediate columns continue from the previous result.
		const start = runningTotal;
		const end = runningTotal + value;

		runningTotal = end;

		result.push({
			...item,
			key,
			name: labels[key],
			value,
			type: value >= 0 ? "increase" : "decrease",
			color: value >= 0 ? theme.palette.error.light : theme.palette.success.light,
			waterfallRange: [Math.min(start, end), Math.max(start, end)],
		});

		return result;
	}, []);
};
